import {
  Box,
  Stack,
  Tabs,
  Tab,
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { ByzMuiDateTimePicker } from "src/components/core-components/mui/inputs/ByzMuiDateTimePicker";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import { useTranslate } from "src/locales/use-locales";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import { useEffect, useState, useCallback, useRef } from "react";
import ByzMcParameterDetailSelect from "src/components/shared-components/ByzMcParameterDetailSelect";
import { useAppSelector } from "src/app/hooks";
import { useCardForm } from "src/hooks/useCardForm";
import { OtherPrivilegeGuard } from "src/utils/authorization/OtherPrivilegeGuard";
import VteRiskFactorDescription from "./components/VteRiskFactorDescription";
import { referenceService } from "src/api/references/referenceService";
import ByzMuiSnackBar from "src/components/core-components/mui/ByzMuiSnackBar";

interface VteRiskFormData {
  VRFE_ID: string;
  PROCESS_TIME: Date | null;
  CTIME: Date | null;
  CUSER: string;
  GENERAL_NOTE: string;
  CALC_VALUE: number;
  HEIGHT: number;
  WEIGHT: number;
  VKI: number;
  AGE_PD_ID: number | null;
  MINOR_SURGERY: number | null;
  PASSED_MAJOR_SURGERY: number | null;
  VARICOSE_VEINS: number | null;
  INFLMMATRY_INTSTL_DISSE_STRY: number | null;
  LEG_SWELLING_PRESENCE: number | null;
  VKI_PD_ID: number | null;
  ACUTE_MYOCARDIAL_INFECTION: number | null;
  CONGESTIVE_HEART_FAILURE: number | null;
  DIABETES_MELLITUS_HYPERTENSION: number | null;
  SEPSIS: number | null;
  PNEUMONIA: number | null;
  SERIOUS_PULMONARY_DISEASE: number | null;
  CHRNC_OBSTRCTVE_PLMNRY_DISEASE: number | null;
  PATIENT_REQUIRING_BED_REST: number | null;
  BRTH_CNTRL_PHRM_OR_HRMN_USE: number | null;
  PREGNANCY_OR_POSTPARTUM: number | null;
  MISCARRIAGE_OR_STILLBIRTH: number | null;
  SPINE_SURGERY: number | null;
  ARTHROSCOPIC_SURGERY: number | null;
  ENDOSCOPIC_UROLOGICL_SURGERY: number | null;
  MALIGNANCY: number | null;
  MAJOR_SURGERY: number | null;
  LAPAROSCOPIC_SURGERY: number | null;
  BEDRIDDEN_PATIENT: number | null;
  IMMOBILIZATION: number | null;
  CENTRAL_VENOUS_CATATER: number | null;
  PATIENT_USING_ANTIAGREGAN: number | null;
  OPERA_HEART_SURGERY: number | null;
  DVT_PE_STORY: number | null;
  FAMILY_THROMBOSIS_STORY: number | null;
  HEPARININDUCED_THROMBCYTOPNI: number | null;
  CONJENTIAL_THROMBOPHILE_FACTRS: number | null;
  MYELOPROLIFERATIVE_DISEASE: number | null;
  ELKTF_MJR_XTRMT_RTRPLY_SURGERY: number | null;
  HIP_PLVS_R_LWR_XTRMTY_FRACTURE: number | null;
  STROKE: number | null;
  MULTIPL_TRAUMA: number | null;
  ACUTE_SPINAL_CORD_INJURY: number | null;
}

interface VteRiskProps {
  recordId?: string;
  patientId: number;
  onClose?: () => void;
  getComId: () => Promise<number | null>;
  comId: number;
  setComId: (comId: number) => void;
  getCardTableData: (comId: number) => Promise<string | "">;
  createTableMasterId: () => Promise<string | null>;
  save: (id: string) => Promise<void>;
  handleChange: (field: keyof VteRiskFormData, value: any) => void;
  patientData: any;
  isSaving?: boolean;
  extraAccess?: boolean;
}

function VteRiskFactorEvaluationCreate({
  recordId,
  save,
  handleChange: parentHandleChange,
  patientData,
  isSaving,
  extraAccess,
  getComId,
  getCardTableData,
  createTableMasterId,
}: VteRiskProps) {
  const { t } = useTranslate();
  const { user } = useAppSelector((state) => state.auth);

  const [masterFormData, setMasterFormData] = useState({
    PERIPHERAL_ARTER_DISEASE: 0,
    CHF_MASTER: 0,
    ACUTE_SUPERFICIENT_HEART_FAILR: 0,
    BLEEDING_DIATHESIS: 0,
    HVNG_HAD_HIT_OR_PRSNC_OF_HTS: 0,
    USE_OF_ORAL_ANTICOAGULANTS: 0,
    INTRAOCULAR_INTRACRANAIL_SRGRY: 0,
    CEREBRAL_HEMORRHAGE_STORY: 0,
    TROMBOSIT: 0,
    HYPRTNSV_CRISIS_UNCTRLD_HYPRTN: 0,
    ACTV_PTC_ULSR_GSTR_INTL_BLDNG: 0,
    BACTERIAL_ENDOCARDITIS: 0,
    LST_24_HRS_FNC_OR_EPDRL_ANSTHS: 0,
    KERTIN_CLANNS_LOW_DOSE_ST_GFR: 0,
    PROPHYLAXIS_DECISION: "",
  });

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<"success" | "error">("error");

  useEffect(() => {
    const fetchMasterData = async () => {
      try {
        const _comId = await getComId();
        if (!_comId) return;

        const response = await referenceService.card.getCardTableData({
          tableName: "MC_VTE_RISK_FACTOR_EVAL",
          columnName: "COM_ID",
          columnValue: _comId,
        });

        if (response && response.data && response.data.length > 0) {
          const data = response.data[0] as any;
          setMasterFormData({
            PERIPHERAL_ARTER_DISEASE: Number(data.PERIPHERAL_ARTER_DISEASE || 0),
            CHF_MASTER: Number(data.CONGESTIVE_HEART_FAILURE || 0),
            ACUTE_SUPERFICIENT_HEART_FAILR: Number(data.ACUTE_SUPERFICIENT_HEART_FAILR || 0),
            BLEEDING_DIATHESIS: Number(data.BLEEDING_DIATHESIS || 0),
            HVNG_HAD_HIT_OR_PRSNC_OF_HTS: Number(data.HVNG_HAD_HIT_OR_PRSNC_OF_HTS || 0),
            USE_OF_ORAL_ANTICOAGULANTS: Number(data.USE_OF_ORAL_ANTICOAGULANTS || 0),
            INTRAOCULAR_INTRACRANAIL_SRGRY: Number(data.INTRAOCULAR_INTRACRANAIL_SRGRY || 0),
            CEREBRAL_HEMORRHAGE_STORY: Number(data.CEREBRAL_HEMORRHAGE_STORY || 0),
            TROMBOSIT: Number(data.TROMBOSIT || 0),
            HYPRTNSV_CRISIS_UNCTRLD_HYPRTN: Number(data.HYPRTNSV_CRISIS_UNCTRLD_HYPRTN || 0),
            ACTV_PTC_ULSR_GSTR_INTL_BLDNG: Number(data.ACTV_PTC_ULSR_GSTR_INTL_BLDNG || 0),
            BACTERIAL_ENDOCARDITIS: Number(data.BACTERIAL_ENDOCARDITIS || 0),
            LST_24_HRS_FNC_OR_EPDRL_ANSTHS: Number(data.LST_24_HRS_FNC_OR_EPDRL_ANSTHS || 0),
            KERTIN_CLANNS_LOW_DOSE_ST_GFR: Number(data.KERTIN_CLANNS_LOW_DOSE_ST_GFR || 0),
            PROPHYLAXIS_DECISION: data.PROPHYLAXIS_DECISION || "",
          });
        }
      } catch (error) {
        console.error("Error fetching master data:", error);
      }
    };

    fetchMasterData();
  }, [getComId, recordId]);

  const handleMasterChange = (field: string, value: any) => {
    setMasterFormData(prev => ({ ...prev, [field]: value }));
  };

  const {
    formData,
    selectedMCPs,
    handleChange,
    handleMCPChange,
    resetForm,
    cardData,
    isEditMode,
    getMCPParameterDetails,
    getScoreValue,
    mcpCacheLoaded,
  } = useCardForm({
    cardType: "VTE_RISK_FACTOR_EVALUATION",
    recordId,
    user,
    onLoadRecord: (record) => {
      Object.entries(record).forEach(([key, value]) => {
        parentHandleChange(key as keyof VteRiskFormData, value);
      });
    },
  });

  const typedFormData = formData as unknown as VteRiskFormData;

  const processedInitialData = useRef<string | null>(null);

  useEffect(() => {
    const recordKey = `${isEditMode ? recordId : 'new'}-${patientData?.data?.pt_id}`;
    if (processedInitialData.current === recordKey) return;

    if (!isEditMode && patientData?.data && mcpCacheLoaded) {
      processedInitialData.current = recordKey;
      resetForm();
      const patientInfo = patientData.data;

      if (patientInfo.patlength && Number(typedFormData.HEIGHT) !== Number(patientInfo.patlength)) {
        handleChange("HEIGHT", Number(patientInfo.patlength));
      }
      if (patientInfo.weight && Number(typedFormData.WEIGHT) !== Number(patientInfo.weight)) {
        handleChange("WEIGHT", Number(patientInfo.weight));
      }

      const birthdate = patientInfo.birthdate;
      if (birthdate && cardData?.mcpConfig?.AGE_PD_ID) {
        const birthDate = new Date(birthdate);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        const realAge = Math.max(0, age);

        const ageMcpId = cardData.mcpConfig.AGE_PD_ID.mcpId;
        const ageDetails = getMCPParameterDetails(ageMcpId);

        if (ageDetails && ageDetails.length >= 4) {
          let targetAgeOption = null;
          if (realAge >= 44 && realAge <= 64) {
            targetAgeOption = ageDetails[1];
          } else if (realAge >= 65 && realAge <= 74) {
            targetAgeOption = ageDetails[2];
          } else if (realAge >= 75) {
            targetAgeOption = ageDetails[3];
          }

          if (targetAgeOption && String(typedFormData.AGE_PD_ID) !== String(targetAgeOption.id)) {
            handleMCPChange("AGE_PD_ID", targetAgeOption);
          }
        }
      }
    } else if (!isEditMode && mcpCacheLoaded) {
      processedInitialData.current = recordKey;
      resetForm();
    }
  }, [isEditMode, recordId, patientData, mcpCacheLoaded, getMCPParameterDetails, handleMCPChange, cardData, resetForm]);

  const [score, setScore] = useState(0);

  const bmiTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (bmiTimeoutRef.current) clearTimeout(bmiTimeoutRef.current);

    const h = typedFormData.HEIGHT;
    const w = typedFormData.WEIGHT;

    if (h > 0 && w > 0) {
      bmiTimeoutRef.current = setTimeout(() => {
        const heightInMeters = h / 100;
        const bmi = w / (heightInMeters * heightInMeters);
        let roundedBmi = isNaN(bmi) ? 0 : parseFloat(bmi.toFixed(2));

        // Oracle NUMBER(4,2) veya benzeri kısıtlar için VKI değerini sınırla (ORA-01438 önlemi)
        if (roundedBmi > 99.99) {
          roundedBmi = 99.99;
        }

        if (roundedBmi > 0 && typedFormData.VKI !== roundedBmi) {
          handleChange("VKI", roundedBmi);
        }

        if (cardData?.mcpConfig?.VKI_PD_ID) {
          const vkiDetails = getMCPParameterDetails(cardData.mcpConfig.VKI_PD_ID.mcpId);
          if (vkiDetails && vkiDetails.length > 1) {
            if (roundedBmi > 25) {
              const heavyOption = vkiDetails[1];
              if (heavyOption && String(typedFormData.VKI_PD_ID) !== String(heavyOption.id)) {
                handleMCPChange("VKI_PD_ID", heavyOption);
              }
            } else {
              if (typedFormData.VKI_PD_ID && String(typedFormData.VKI_PD_ID) !== "") {
                handleMCPChange("VKI_PD_ID", null);
              }
            }
          }
        }
      }, 600);
    }

    return () => {
      if (bmiTimeoutRef.current) clearTimeout(bmiTimeoutRef.current);
    };
  }, [
    typedFormData.HEIGHT,
    typedFormData.WEIGHT,
    typedFormData.VKI_PD_ID,
    getMCPParameterDetails,
    handleChange,
    handleMCPChange,
    cardData,
  ]);

  const handleScoreCalculate = useCallback(() => {
    if (!cardData) return;
    let total = 0;
    const mcpFields = [
      "AGE_PD_ID", "MINOR_SURGERY", "PASSED_MAJOR_SURGERY", "VARICOSE_VEINS",
      "INFLMMATRY_INTSTL_DISSE_STRY", "LEG_SWELLING_PRESENCE", "ACUTE_MYOCARDIAL_INFECTION",
      "CONGESTIVE_HEART_FAILURE", "DIABETES_MELLITUS_HYPERTENSION", "SEPSIS", "PNEUMONIA",
      "SERIOUS_PULMONARY_DISEASE", "CHRNC_OBSTRCTVE_PLMNRY_DISEASE", "PATIENT_REQUIRING_BED_REST",
      "BRTH_CNTRL_PHRM_OR_HRMN_USE", "PREGNANCY_OR_POSTPARTUM", "MISCARRIAGE_OR_STILLBIRTH",
      "SPINE_SURGERY", "ARTHROSCOPIC_SURGERY", "ENDOSCOPIC_UROLOGICL_SURGERY", "MALIGNANCY",
      "MAJOR_SURGERY", "LAPAROSCOPIC_SURGERY", "BEDRIDDEN_PATIENT", "IMMOBILIZATION",
      "CENTRAL_VENOUS_CATATER", "PATIENT_USING_ANTIAGREGAN", "OPERA_HEART_SURGERY",
      "DVT_PE_STORY", "FAMILY_THROMBOSIS_STORY", "HEPARININDUCED_THROMBCYTOPNI",
      "CONJENTIAL_THROMBOPHILE_FACTRS", "MYELOPROLIFERATIVE_DISEASE",
      "ELKTF_MJR_XTRMT_RTRPLY_SURGERY", "HIP_PLVS_R_LWR_XTRMTY_FRACTURE", "STROKE",
      "MULTIPL_TRAUMA", "ACUTE_SPINAL_CORD_INJURY", "VKI_PD_ID"
    ];

    mcpFields.forEach(field => {
      const val = getScoreValue(field);
      if (val && !isNaN(val)) total += val;
    });

    const finalTotal = isNaN(total) ? 0 : total;

    if (!Object.is(score, finalTotal)) {
      setScore(finalTotal);
      // Puanı yerel form datasına yaz, ebeveyne hemen bildirme
      handleChange("CALC_VALUE", finalTotal);
    }

  }, [cardData, getScoreValue, handleChange, score]);

  useEffect(() => {
    handleScoreCalculate();
  }, [handleScoreCalculate, selectedMCPs]);

  const [currentTab, setCurrentTab] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const [localIsSaving, setLocalIsSaving] = useState(false);

  const syncToParent = useCallback((currentData: Record<string, any>) => {
    Object.entries(currentData).forEach(([key, value]) => {
      let val = value;
      const numVal = Number(value);

      // Veritabanı (Oracle) kısıtlarına göre koruma katmanı
      if (key === "VKI" && !isNaN(numVal) && numVal > 99.99) {
        val = 99.99;
      } else if ((key === "HEIGHT" || key === "WEIGHT") && !isNaN(numVal) && numVal > 999) {
        val = 999;
      } else if (key === "CALC_VALUE" && !isNaN(numVal) && numVal > 99) {
        val = 99;
      } else if (key === "GENERAL_NOTE" && typeof val === "string" && val.length > 200) {
        val = val.substring(0, 200);
      }

      parentHandleChange(key as keyof VteRiskFormData, val);
    });
  }, [parentHandleChange]);

  const handleSave = async (id?: string) => {
    try {
      const hasAnySelection = Object.values(selectedMCPs).some(mcp => mcp !== null);
      if (!hasAnySelection) {
        setAlertMessage(t("vte-risk-factor-evaluation.no-selection-warning"));
        setAlertType("error");
        setAlertOpen(true);
        return;
      }

      setLocalIsSaving(true);

      // Kayıt öncesi tüm yerel datayı ebeveyn forma (CardList) senkronize et
      syncToParent(formData);

      // Ebeveyn state'inin güncellenmesi için bir mikro-task bekle
      await new Promise(resolve => setTimeout(resolve, 100));

      const _comId = await getComId();
      if (!_comId) return;

      let masterId = id;
      if (!masterId || masterId === "") {
        masterId = await getCardTableData(_comId);
        if (!masterId) {
          const newMasterId = await createTableMasterId();
          masterId = newMasterId || "";
        }
      }

      if (!masterId) return;

      const masterColumns = [
        { key: "PERIPHERAL_ARTER_DISEASE", value: String(masterFormData.PERIPHERAL_ARTER_DISEASE) },
        { key: "CONGESTIVE_HEART_FAILURE", value: String(masterFormData.CHF_MASTER) },
        { key: "ACUTE_SUPERFICIENT_HEART_FAILR", value: String(masterFormData.ACUTE_SUPERFICIENT_HEART_FAILR) },
        { key: "BLEEDING_DIATHESIS", value: String(masterFormData.BLEEDING_DIATHESIS) },
        { key: "HVNG_HAD_HIT_OR_PRSNC_OF_HTS", value: String(masterFormData.HVNG_HAD_HIT_OR_PRSNC_OF_HTS) },
        { key: "USE_OF_ORAL_ANTICOAGULANTS", value: String(masterFormData.USE_OF_ORAL_ANTICOAGULANTS) },
        { key: "INTRAOCULAR_INTRACRANAIL_SRGRY", value: String(masterFormData.INTRAOCULAR_INTRACRANAIL_SRGRY) },
        { key: "CEREBRAL_HEMORRHAGE_STORY", value: String(masterFormData.CEREBRAL_HEMORRHAGE_STORY) },
        { key: "TROMBOSIT", value: String(masterFormData.TROMBOSIT) },
        { key: "HYPRTNSV_CRISIS_UNCTRLD_HYPRTN", value: String(masterFormData.HYPRTNSV_CRISIS_UNCTRLD_HYPRTN) },
        { key: "ACTV_PTC_ULSR_GSTR_INTL_BLDNG", value: String(masterFormData.ACTV_PTC_ULSR_GSTR_INTL_BLDNG) },
        { key: "BACTERIAL_ENDOCARDITIS", value: String(masterFormData.BACTERIAL_ENDOCARDITIS) },
        { key: "LST_24_HRS_FNC_OR_EPDRL_ANSTHS", value: String(masterFormData.LST_24_HRS_FNC_OR_EPDRL_ANSTHS) },
        { key: "KERTIN_CLANNS_LOW_DOSE_ST_GFR", value: String(masterFormData.KERTIN_CLANNS_LOW_DOSE_ST_GFR) },
        { key: "PROPHYLAXIS_DECISION", value: masterFormData.PROPHYLAXIS_DECISION },
      ];

      await referenceService.card.put({
        tablename: "MC_VTE_RISK_FACTOR_EVAL",
        id: parseInt(masterId),
        columns: masterColumns,
      });

      await save(masterId);
    } catch (error) {
      console.error("VTE Save Error:", error);
      setAlertMessage(t("card.save-error"));
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLocalIsSaving(false);
    }
  };


  const renderMCPField = (
    fieldName: string,
    labelKey: string,
    mcpId: number
  ) => (
    <Grid container alignItems="center" justifyContent="space-between">
      <Grid
        xs={12}
        sm={6}
        md={6}
        sx={{ borderBottom: "1px solid #E0E0E0" }}
      >
        <ByzMuiTypography sx={{ marginLeft: "30px" }}>
          {t(labelKey)}
        </ByzMuiTypography>
      </Grid>
      <Grid xs={12} sm={6} md={6}>
        <ByzMcParameterDetailSelect
          title={t("select")}
          mcpId={mcpId}
          multiple={false}
          handleChange={(val) => {
            if (!Array.isArray(val)) {
              handleMCPChange(fieldName, val);
            }
          }}
          value={selectedMCPs[fieldName]}
          sx={{ width: "100%" }}
          cachedData={getMCPParameterDetails(mcpId)}
          cacheLoaded={mcpCacheLoaded}
          readOnly={!extraAccess}
        />
      </Grid>
    </Grid>
  );

  return (
    <Box sx={{ padding: "20px", width: "100%" }}>
      <Box mb={3}>
        <OtherPrivilegeGuard
          extraAccess={!!extraAccess}
          fallback={
            <ByzMuiSaveButton
              disabled
              tooltip={!extraAccess ? t("no-permission-for-this-action") : undefined}
            />
          }
        >
          <ByzMuiSaveButton
            onClick={() => handleSave(typedFormData.VRFE_ID)}
            loading={isSaving || localIsSaving}
          />
        </OtherPrivilegeGuard>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab
            label={t("vte-risk-factor-evaluation.point-calculation", "capitalize")}
          />
          <Tab label={t("description", "capitalize")} />
        </Tabs>
      </Box>

      {currentTab === 0 && (
        <Grid container spacing={2}>
          <Grid
            xs={12}
            sm={8}
            md={8}
            container
            direction={"column"}
            spacing={3}
          >
            <Grid
              xs={12}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
                mb: 2
              }}
            >
              <ByzMuiTypography isTitle={true}>
                {t("vte-risk-factor-evaluation.vte-risk-factor-evaluation")}
              </ByzMuiTypography>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid xs={12} sm={6} md={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>{t("datetime")}</ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMuiDateTimePicker
                  sx={{ width: "100%" }}
                  label={t("datetime")}
                  value={typedFormData.PROCESS_TIME ? new Date(typedFormData.PROCESS_TIME) : null}
                  onChange={(e) => handleChange("PROCESS_TIME", e)}
                  disabled={!extraAccess}
                  maxDate={new Date()}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid xs={12} sm={6} md={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>{t("createdby")}</ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  disabled
                  value={typedFormData.CUSER}
                />
              </Grid>
            </Grid>

            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={true}>
                {t("vte-risk-factor-evaluation.current-risk-factors")}
              </ByzMuiTypography>
            </Grid>

            {renderMCPField("AGE_PD_ID", "vte-risk-factor-evaluation.age", 485)}

            {renderMCPField("MINOR_SURGERY", "vte-risk-factor-evaluation.minor-surgery", 486)}

            {renderMCPField("PASSED_MAJOR_SURGERY", "vte-risk-factor-evaluation.passed-major-surgery", 487)}

            {renderMCPField("VARICOSE_VEINS", "vte-risk-factor-evaluation.varicose-veins", 488)}

            {renderMCPField("INFLMMATRY_INTSTL_DISSE_STRY", "vte-risk-factor-evaluation.inflammatory-intestinal-disease", 489)}

            {renderMCPField("LEG_SWELLING_PRESENCE", "vte-risk-factor-evaluation.leg-swelling", 490)}

            {renderMCPField("ACUTE_MYOCARDIAL_INFECTION", "vte-risk-factor-evaluation.acute-myocardial-infarction", 492)}

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid md={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                <Grid container spacing={0} justifyContent="space-between">
                  <Grid xs={4} sx={{ pl: 2, textAlign: "center" }}>
                    <ByzMuiTypography variant="caption">{t("vte-risk-factor-evaluation.height")}</ByzMuiTypography>
                  </Grid>
                  <Grid xs={4} sx={{ borderLeft: "1px solid #E0E0E0", pl: 2, textAlign: "center" }}>
                    <ByzMuiTypography variant="caption">{t("vte-risk-factor-evaluation.weight")}</ByzMuiTypography>
                  </Grid>
                  <Grid xs={4} sx={{ borderLeft: "1px solid #E0E0E0", pl: 2, textAlign: "left" }}>
                    <ByzMuiTypography>{t("vte-risk-factor-evaluation.bmi")}</ByzMuiTypography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid md={6} xs={6}>
                <Stack direction="row" spacing={2} sx={{ width: "100%" }}>
                  <ByzMuiTextField
                    type="number"
                    value={typedFormData.HEIGHT || ""}
                    onChange={(e) => {
                      const val = Number(e.target.value);
                      handleChange("HEIGHT", val > 999 ? 999 : val);
                    }}
                    onInput={(e: any) => {
                      e.target.value = e.target.value.slice(0, 3);
                    }}
                    disabled={!extraAccess}
                    size="small"
                    label={t("enter")}
                    sx={{ flex: 1 }}
                  />
                  <ByzMuiTextField
                    type="number"
                    value={typedFormData.WEIGHT || ""}
                    onChange={(e) => {
                      const val = Number(e.target.value);
                      handleChange("WEIGHT", val > 999 ? 999 : val);
                    }}
                    onInput={(e: any) => {
                      e.target.value = e.target.value.slice(0, 3);
                    }}
                    disabled={!extraAccess}
                    size="small"
                    label={t("enter")}
                    sx={{ flex: 1 }}
                  />
                  <ByzMuiTextField
                    value={typedFormData.VKI || ""}
                    cantChange
                    size="small"
                    label={t("vte-risk-factor-evaluation.vki")}
                    sx={{ flex: 1 }}
                  />
                </Stack>
              </Grid>
            </Grid>

            {renderMCPField("VKI_PD_ID", "vte-risk-factor-evaluation.bmi-score", 491)}

            {renderMCPField("CONGESTIVE_HEART_FAILURE", "vte-risk-factor-evaluation.congestive-heart-failure", 493)}

            {renderMCPField("DIABETES_MELLITUS_HYPERTENSION", "vte-risk-factor-evaluation.diabetes-hypertension", 494)}

            {renderMCPField("SEPSIS", "vte-risk-factor-evaluation.sepsis", 495)}

            {renderMCPField("PNEUMONIA", "vte-risk-factor-evaluation.pneumonia", 496)}

            {renderMCPField("SERIOUS_PULMONARY_DISEASE", "vte-risk-factor-evaluation.serious-pulmonary-disease", 497)}

            {renderMCPField("CHRNC_OBSTRCTVE_PLMNRY_DISEASE", "vte-risk-factor-evaluation.copd", 498)}

            {renderMCPField("PATIENT_REQUIRING_BED_REST", "vte-risk-factor-evaluation.bed-rest-required", 499)}

            {renderMCPField("BRTH_CNTRL_PHRM_OR_HRMN_USE", "vte-risk-factor-evaluation.birth-control-hormone-use", 500)}

            {renderMCPField("PREGNANCY_OR_POSTPARTUM", "vte-risk-factor-evaluation.pregnancy-postpartum", 501)}

            {renderMCPField("MISCARRIAGE_OR_STILLBIRTH", "vte-risk-factor-evaluation.miscarriage-stillbirth", 502)}

            {renderMCPField("SPINE_SURGERY", "vte-risk-factor-evaluation.spine-surgery", 503)}

            {renderMCPField("ARTHROSCOPIC_SURGERY", "vte-risk-factor-evaluation.arthroscopic-surgery", 504)}

            {renderMCPField("ENDOSCOPIC_UROLOGICL_SURGERY", "vte-risk-factor-evaluation.endoscopic-urologic-surgery", 505)}

            {renderMCPField("MALIGNANCY", "vte-risk-factor-evaluation.malignancy", 506)}

            {renderMCPField("MAJOR_SURGERY", "vte-risk-factor-evaluation.major-surgery", 507)}

            {renderMCPField("LAPAROSCOPIC_SURGERY", "vte-risk-factor-evaluation.laparoscopic-surgery", 508)}

            {renderMCPField("BEDRIDDEN_PATIENT", "vte-risk-factor-evaluation.bedridden-patient", 509)}

            {renderMCPField("IMMOBILIZATION", "vte-risk-factor-evaluation.immobilization", 510)}

            {renderMCPField("CENTRAL_VENOUS_CATATER", "vte-risk-factor-evaluation.central-venous-catheter", 511)}

            {renderMCPField("PATIENT_USING_ANTIAGREGAN", "vte-risk-factor-evaluation.patient-using-antiaggregant", 512)}

            {renderMCPField("OPERA_HEART_SURGERY", "vte-risk-factor-evaluation.open-heart-surgery", 513)}

            {renderMCPField("DVT_PE_STORY", "vte-risk-factor-evaluation.dvt-pe-history", 514)}

            {renderMCPField("FAMILY_THROMBOSIS_STORY", "vte-risk-factor-evaluation.family-thrombosis-history", 515)}

            {renderMCPField("HEPARININDUCED_THROMBCYTOPNI", "vte-risk-factor-evaluation.heparin-induced-thrombocytopenia", 516)}

            {renderMCPField("CONJENTIAL_THROMBOPHILE_FACTRS", "vte-risk-factor-evaluation.congenital-thrombophilia", 517)}

            {renderMCPField("MYELOPROLIFERATIVE_DISEASE", "vte-risk-factor-evaluation.myeloproliferative-disease", 518)}

            {renderMCPField("ELKTF_MJR_XTRMT_RTRPLY_SURGERY", "vte-risk-factor-evaluation.elective-major-arthroplasty", 519)}

            {renderMCPField("HIP_PLVS_R_LWR_XTRMTY_FRACTURE", "vte-risk-factor-evaluation.hip-pelvis-fracture", 520)}

            {renderMCPField("STROKE", "vte-risk-factor-evaluation.stroke", 521)}

            {renderMCPField("MULTIPL_TRAUMA", "vte-risk-factor-evaluation.multiple-trauma", 522)}

            {renderMCPField("ACUTE_SPINAL_CORD_INJURY", "vte-risk-factor-evaluation.acute-spinal-cord-injury", 523)}

            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={true}>
                {t("vte-risk-factor-evaluation.other")}
              </ByzMuiTypography>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid xs={12} sm={6} md={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>{t("vte-risk-factor-evaluation.score")}</ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMuiTextField
                  value={score}
                  disabled
                  sx={{ width: '100%' }}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid xs={12} sm={6} md={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>{t("vte-risk-factor-evaluation.note")}</ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMuiTextField
                  value={typedFormData.GENERAL_NOTE || ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    handleChange("GENERAL_NOTE", val.length > 200 ? val.substring(0, 200) : val);
                  }}
                  disabled={!extraAccess}
                  sx={{ width: '100%' }}
                  multiline
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}

      {currentTab === 1 && (
        <Box>
          <VteRiskFactorDescription
            formData={typedFormData}
            masterFormData={masterFormData}
            handleChange={handleChange}
            handleMasterChange={handleMasterChange}
            extraAccess={extraAccess}
          />
        </Box>
      )}
      <ByzMuiSnackBar
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
        title={t("warning")}
        message={alertMessage}
        type={alertType}
      />
    </Box>
  );
}

export default VteRiskFactorEvaluationCreate;
